import { useState, useCallback } from 'react';
import { scoutMessageService } from '../services/scoutMessageService';
import {
  SendScoutMessageRequest,
  ScoutHistoryResponse,
  ScoutQuotaResponse,
  ScoutMessageResult,
  MessageQualityScore,
} from '../types/scout-message';
import { useToast } from './useToast';

interface UseScoutMessageReturn {
  // States
  isLoading: boolean;
  error: string | null;

  // Send Message
  sendMessage: (request: SendScoutMessageRequest) => Promise<boolean>;

  // History
  history: ScoutMessageResult[];
  historyStats: any;
  historyPagination: any;
  loadHistory: (page?: number) => Promise<void>;

  // Quota
  quota: any;
  loadQuota: () => Promise<void>;

  // Quality Check
  calculateQuality: (message: string, recipient?: any) => MessageQualityScore;
}

export const useScoutMessage = (): UseScoutMessageReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<ScoutMessageResult[]>([]);
  const [historyStats, setHistoryStats] = useState<any>(null);
  const [historyPagination, setHistoryPagination] = useState<any>(null);
  const [quota, setQuota] = useState<any>(null);

  const { showToast } = useToast();

  const loadHistory = useCallback(
    async (page: number = 1): Promise<void> => {
      setIsLoading(true);
      setError(null);

      try {
        // まず企業情報を取得してcompanyIdを取得
        const companyInfoResponse = await fetch('/api/users/me/company', {
          credentials: 'include',
        });

        if (!companyInfoResponse.ok) {
          throw new Error('企業情報の取得に失敗しました');
        }

        const companyInfo = await companyInfoResponse.json();
        const companyId = companyInfo.data.companyId;

        const response = await scoutMessageService.getHistory(companyId, page);
        setHistory(response.data.messages);
        setHistoryStats(response.data.stats);
        setHistoryPagination(response.data.pagination);
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'スカウト履歴の取得に失敗しました';
        setError(errorMessage);
        showToast(errorMessage, 'error');
      } finally {
        setIsLoading(false);
      }
    },
    [showToast]
  );

  const loadQuota = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      // まず企業情報を取得してcompanyIdを取得
      const companyInfoResponse = await fetch('/api/users/me/company', {
        credentials: 'include',
      });

      if (!companyInfoResponse.ok) {
        throw new Error('企業情報の取得に失敗しました');
      }

      const companyInfo = await companyInfoResponse.json();
      const companyId = companyInfo.data.companyId;

      const response = await scoutMessageService.getQuota(companyId);
      setQuota(response);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : '送信制限情報の取得に失敗しました';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  const sendMessage = useCallback(
    async (request: SendScoutMessageRequest): Promise<boolean> => {
      setIsLoading(true);
      setError(null);

      try {
        // まず企業情報を取得してcompanyIdを取得
        const companyInfoResponse = await fetch('/api/users/me/company', {
          credentials: 'include',
        });

        if (!companyInfoResponse.ok) {
          throw new Error('企業情報の取得に失敗しました');
        }

        const companyInfo = await companyInfoResponse.json();
        const companyId = companyInfo.data.companyId;

        // リクエストにcompanyIdを設定
        const requestWithCompanyId = { ...request, companyId };

        const response =
          await scoutMessageService.sendMessage(requestWithCompanyId);
        showToast('スカウトメッセージを送信しました', 'success');

        // 送信後に履歴を更新
        await loadHistory();
        await loadQuota();

        return true;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'スカウトメッセージの送信に失敗しました';
        setError(errorMessage);
        showToast(errorMessage, 'error');
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [showToast, loadHistory, loadQuota]
  );

  const calculateQuality = useCallback(
    (message: string, recipient?: any): MessageQualityScore => {
      let score = 0;
      let personalization = 0;
      let appropriateness = 80; // Base appropriateness score
      let clarity = 70; // Base clarity score
      let lengthScore = 0;
      let spamRisk = 0;

      // Length scoring
      if (message.length >= 50 && message.length <= 300) {
        lengthScore = 90;
      } else if (message.length > 300 && message.length <= 600) {
        lengthScore = 100;
      } else if (message.length > 600 && message.length <= 1000) {
        lengthScore = 85;
      } else {
        lengthScore = 50;
      }

      // Personalization scoring
      if (recipient) {
        if (message.includes(recipient.name)) personalization += 30;
        if (recipient.university && message.includes(recipient.university))
          personalization += 20;
        if (recipient.faculty && message.includes(recipient.faculty))
          personalization += 15;
      }

      // Basic quality indicators
      const sentences = message.split('。').filter((s) => s.trim().length > 0);
      if (sentences.length >= 3) clarity += 10;
      if (message.includes('お疲れ様') || message.includes('よろしく'))
        appropriateness += 10;

      // Spam risk detection
      const exclamationCount = (message.match(/!/g) || []).length;
      if (exclamationCount > 3) spamRisk += exclamationCount * 10;

      const urgentWords = ['今すぐ', '急募', '限定', '特別'];
      const urgentCount = urgentWords.filter((word) =>
        message.includes(word)
      ).length;
      spamRisk += urgentCount * 20;

      const capsWords = (message.match(/[A-Z]{3,}/g) || []).length;
      spamRisk += capsWords * 15;

      spamRisk = Math.min(spamRisk, 100);

      // Overall score calculation
      score = Math.round(
        (personalization + appropriateness + clarity + lengthScore) / 4
      );

      return {
        overall: Math.max(0, Math.min(100, score)),
        personalization: Math.max(0, Math.min(100, personalization)),
        appropriateness: Math.max(0, Math.min(100, appropriateness)),
        clarity: Math.max(0, Math.min(100, clarity)),
        length: Math.max(0, Math.min(100, lengthScore)),
        spamRisk: Math.max(0, Math.min(100, spamRisk)),
      };
    },
    []
  );

  return {
    isLoading,
    error,
    sendMessage,
    history,
    historyStats,
    historyPagination,
    loadHistory,
    quota,
    loadQuota,
    calculateQuality,
  };
};
